import {
    ChangeEvent,
    Dispatch,
    FC,
    useCallback,
    useMemo,
    useState,
} from "react";
import { WidthFormControl } from "../../../models/models";
import { licenseStatus } from "../../../pages/LicenseKeys/helpers";
import { SetStateAction } from "../../../models/models.helpers";
import SearchInput, {
    SearchInputValueHandler,
} from "../../../assets/components/Inputs/SearchInput/SearchInput";
import {
    ActiveOptionsStatus,
    OptionSearchBarSelectStatus,
} from "./SearchBarSelectStatus/helpers";
import SearchBarSearchBarSelectStatus, {
    ActiveOptionsStatusHandler,
} from "./SearchBarSelectStatus/SearchBarSelectStatus";
import {
    ActiveOption,
    OptionList,
    ActiveOptionHandler,
} from "../../helpers/helpers";
import { SearchBarSelect } from "./SearchBarSelect/SearchBarSelect";
import SearchBarMultiSelect, {
    MultiSelectIdsHandler,
} from "./SearchBarMultiSelect/SearchBarMultiSelect";
import DataPiker from "../DataPiker/DataPiker";

import "./SearchBar.scss";
import Checkbox from "../Checkbox/Checkbox";
import { handleRangeSelect } from "./SearchBarHelpers";
export interface DateRange {
    startDate: number | undefined;
    endDate: number | undefined;
}
export interface SearchBarDataHandler {
    searchInput?: string;
    searchByOrderId?: string;
    status?: ActiveOptionsStatus;
    userId?: number;
    productId?: number;
    userIds?: number[];
    productIds?: number[];
    startDate?: number;
    endDate?: number;
    nextRebillStartDate?: number;
    nextRebillEndDate?: number;
    sortNextRebillDate?: boolean;
    projectId?: number;
    isMigrated?: boolean;
    targetArea?: number;
    changeType?: number;
}

type SearchBarProps<T> = {
    initialData?: {
        dateRange?: DateRange;
    };
    usersOptions?: OptionList;
    projectOptions?: OptionList;
    productOptions?: OptionList;
    dataHandler: Dispatch<SetStateAction<T>>;
    targetAreaOptions?: OptionList;
    changeTypeOptions?: OptionList;
    dateRangeOptions?: OptionList;
    migratedStatusCheckbox?: {
        name: string;
        leftLabel?: string;
        checked: boolean;
    };
    migratedLicenseOptions?: OptionList;
    statusOptions?: OptionSearchBarSelectStatus[];
    multiSelect?: {
        users?: boolean;
        products?: boolean;
    };
    searchInput?: {
        placeholder: string;
    };
    searchByOrderId?: {
        placeholder: string;
    };
    showComponents?: {
        selectStatus?: boolean;
        dataPikerDateCreated?: boolean;
        dataPikerNextRebillDate?: boolean;
        searchInput?: boolean;
        dataPikerHistoryActionDate?: boolean;
        dataPikerDateRange?: boolean;
    };
};

export const SearchBar: FC<SearchBarProps<SearchBarDataHandler>> = ({
    initialData: data,
    usersOptions,
    projectOptions,
    productOptions,
    dataHandler,
    searchInput,
    searchByOrderId,
    migratedStatusCheckbox,
    migratedLicenseOptions,
    statusOptions,
    changeTypeOptions,
    targetAreaOptions,
    dateRangeOptions,
    multiSelect,
    showComponents = {
        searchInput: false,
        selectStatus: false,
        dataPikerDateCreated: false,
        dataPikerHistoryActionDate: false,
        dataPikerDateRange: false,
    },
}) => {
    const [checked, setChecked] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

    const optionsStatusSelect = useMemo(() => {
        if (statusOptions) return statusOptions;
        return Object.values(licenseStatus).map(
            ({ color, name, status }, id): OptionSearchBarSelectStatus => ({
                id,
                status,
                name,
                color,
                checked: true,
            })
        );
    }, [statusOptions]);

    const onChangeSearchInputHandler: SearchInputValueHandler = useCallback(
        (value: string | undefined) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    searchInput: value,
                };
            });
        },
        [dataHandler]
    );

    const onChangeSearchByOrderIdHandler: SearchInputValueHandler = useCallback(
        (value: string | undefined) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    searchByOrderId: value,
                };
            });
        },
        [dataHandler]
    );

    const activeOptionsStatusHandler: ActiveOptionsStatusHandler = useCallback(
        (activeOptionsStatus: ActiveOptionsStatus) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    status: activeOptionsStatus,
                };
            });
        },
        [dataHandler]
    );

    const activeOptionsSelectHandler1: ActiveOptionHandler = useCallback(
        (activeOption: ActiveOption) => {
            dataHandler((prevData) => ({
                ...prevData,
                userId: activeOption?.id,
            }));
        },
        [dataHandler]
    );

    const activeOptionsSelectHandler2: ActiveOptionHandler = useCallback(
        (activeOption: ActiveOption) => {
            dataHandler((prevData) => {
                const keyToUpdate = projectOptions ? "projectId" : "productId";
                return {
                    ...prevData,
                    [keyToUpdate]: activeOption?.id,
                };
            });
        },
        [dataHandler, projectOptions]
    );

    const userIdsHandler: MultiSelectIdsHandler = useCallback(
        (ids: number[]) => {
            setSelectedUserIds(ids);
            dataHandler((prevData) => ({
                ...prevData,
                userIds: ids.length ? ids : undefined,
            }));
        },
        [dataHandler]
    );

    const productIdsHandler: MultiSelectIdsHandler = useCallback(
        (ids: number[]) => {
            setSelectedProductIds(ids);
            dataHandler((prevData) => ({
                ...prevData,
                productIds: ids.length ? ids : undefined,
            }));
        },
        [dataHandler]
    );

    const selectHandlerFilterMigratedLicense: ActiveOptionHandler = useCallback(
        (activeOption: ActiveOption) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,

                    isMigrated: activeOption?.data as boolean | undefined,
                };
            });
        },
        [dataHandler]
    );

    const handleStartDateChange = useCallback(
        (startDate: number | undefined) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    startDate: startDate,
                };
            });
        },
        [dataHandler]
    );

    const handleEndDateChange = useCallback(
        (endDate: number | undefined) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    endDate: endDate,
                };
            });
        },
        [dataHandler]
    );

    const handleNextRebillDateChange = useCallback(
        (startDate: number | undefined) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    nextRebillStartDate: startDate,
                };
            });
        },
        [dataHandler]
    );

    const handleNextRebillDateEndChange = useCallback(
        (endDate: number | undefined) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    nextRebillEndDate: endDate,
                };
            });
        },
        [dataHandler]
    );

    const onChangeCheckboxHandler = useCallback(
        (
            event: React.ChangeEvent<HTMLInputElement>
        ): ChangeEvent<HTMLInputElement> => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    isMigrated: event.target.checked,
                };
            });

            return event;
        },
        [dataHandler]
    );

    const activeOptionsSelectTargetArea: ActiveOptionHandler = useCallback(
        (activeOption: ActiveOption) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    targetArea: activeOption?.id,
                };
            });
        },
        [dataHandler]
    );

    const activeOptionsSelectChangeType: ActiveOptionHandler = useCallback(
        (activeOption: ActiveOption) => {
            dataHandler((prevData) => {
                return {
                    ...prevData,
                    changeType: activeOption?.id,
                };
            });
        },
        [dataHandler]
    );

    const activeOptionsSelectRangeDate: ActiveOptionHandler = useCallback(
        (activeOption: ActiveOption) => {
            if (!activeOption) {
                dataHandler((prevData) => {
                    return {
                        ...prevData,
                        startDate: undefined,
                        endDate: undefined,
                    };
                });
                return;
            }

            const range = handleRangeSelect(activeOption);
            if (range) {
                const { from, to } = range;

                dataHandler((prevData) => {
                    return {
                        ...prevData,
                        startDate: from,
                        endDate: to,
                    };
                });
            }
        },
        [dataHandler]
    );

    return (
        <div className="search_bar">
            <div className="search_bar__row">
                {searchInput?.placeholder && (
                    <SearchInput
                        placeholder={searchInput.placeholder}
                        valueHandler={onChangeSearchInputHandler}
                        widthFormControlWidth={WidthFormControl.NORMAL}
                    />
                )}

                {searchByOrderId?.placeholder && (
                    <SearchInput
                        placeholder={searchByOrderId.placeholder}
                        valueHandler={onChangeSearchByOrderIdHandler}
                        widthFormControlWidth={WidthFormControl.SMALL}
                    />
                )}

                {showComponents.dataPikerHistoryActionDate && (
                    <DataPiker
                        leftLabel="Action Date"
                        startDateHandler={handleStartDateChange}
                        endDateHandler={handleEndDateChange}
                    />
                )}

                {usersOptions &&
                    (multiSelect?.users ? (
                        <SearchBarMultiSelect
                            label="User All"
                            placeholder="User All"
                            options={usersOptions}
                            selectedIds={selectedUserIds}
                            onChange={userIdsHandler}
                        />
                    ) : (
                        <SearchBarSelect
                            widthFormControl={WidthFormControl.XS_SMALL}
                            name="User All"
                            options={usersOptions}
                            activeOptionHandler={activeOptionsSelectHandler1}
                        />
                    ))}

                {targetAreaOptions && (
                    <SearchBarSelect
                        widthFormControl={WidthFormControl.X_SMALL}
                        name="Target Area"
                        options={targetAreaOptions}
                        activeOptionHandler={activeOptionsSelectTargetArea}
                    />
                )}

                {changeTypeOptions && (
                    <SearchBarSelect
                        widthFormControl={WidthFormControl.X_SMALL}
                        name="Action type"
                        options={changeTypeOptions}
                        activeOptionHandler={activeOptionsSelectChangeType}
                    />
                )}

                {productOptions &&
                    (multiSelect?.products ? (
                        <SearchBarMultiSelect
                            label="Product All"
                            placeholder="Product All"
                            options={productOptions}
                            selectedIds={selectedProductIds}
                            onChange={productIdsHandler}
                        />
                    ) : (
                        <SearchBarSelect
                            widthFormControl={WidthFormControl.X_SMALL}
                            name="Product All"
                            options={productOptions}
                            activeOptionHandler={activeOptionsSelectHandler2}
                        />
                    ))}

                {!productOptions && projectOptions && (
                    <SearchBarSelect
                        widthFormControl={WidthFormControl.X_SMALL}
                        name="Project All"
                        options={projectOptions}
                        activeOptionHandler={activeOptionsSelectHandler2}
                    />
                )}

                {dateRangeOptions && (
                    <SearchBarSelect
                        widthFormControl={WidthFormControl.X_SMALL}
                        name="Data Range"
                        options={dateRangeOptions}
                        resetTrigger={
                            data?.dateRange?.startDate === undefined ||
                            data?.dateRange?.endDate === undefined
                        }
                        activeOptionHandler={activeOptionsSelectRangeDate}
                    />
                )}

                {migratedLicenseOptions && (
                    <SearchBarSelect
                        widthFormControl={WidthFormControl.X_SMALL}
                        name={"License All"}
                        options={migratedLicenseOptions}
                        activeOptionHandler={selectHandlerFilterMigratedLicense}
                    />
                )}

                {showComponents.selectStatus && (
                    <SearchBarSearchBarSelectStatus
                        name="Status All"
                        options={optionsStatusSelect}
                        activeOptionsStatusHandler={activeOptionsStatusHandler}
                        widthFormControl={WidthFormControl.X_SMALL}
                    />
                )}

                {migratedStatusCheckbox && (
                    <Checkbox
                        className="search_bar_checkbox"
                        name="checkbox1"
                        leftLabel={migratedStatusCheckbox?.leftLabel}
                        onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                        ): ChangeEvent<HTMLInputElement> => {
                            setChecked(!checked);
                            onChangeCheckboxHandler(e);
                            return e;
                        }}
                        checked={checked}
                        value={checked.toString()}
                    />
                )}
            </div>

            {showComponents.dataPikerDateRange && (
                <div className="search_bar__row">
                    <DataPiker
                        leftLabel="Order Date"
                        endDate={data?.dateRange?.endDate}
                        startDate={data?.dateRange?.startDate}
                        startDateHandler={handleStartDateChange}
                        endDateHandler={handleEndDateChange}
                    />
                </div>
            )}

            {(showComponents.dataPikerDateCreated ||
                showComponents.dataPikerNextRebillDate) && (
                <div className="search_bar__row">
                    {showComponents.dataPikerDateCreated && (
                        <DataPiker
                            leftLabel="Date Created"
                            startDateHandler={handleStartDateChange}
                            endDateHandler={handleEndDateChange}
                        />
                    )}

                    {showComponents.dataPikerNextRebillDate && (
                        <DataPiker
                            leftLabel="Next Rebill Date"
                            startDateHandler={handleNextRebillDateChange}
                            endDateHandler={handleNextRebillDateEndChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
